import { type ChangeEvent, useState } from 'react';
import translations from '../../../../../../../../translations';
import type {
    Confirmation,
    ListFilter,
    ObjectDataRequest,
} from '../../../../../../../../types/generic';
import type { FilterType } from '../../../../../../../../types/pageEditor';
import type { ValueElementIdAPI } from '../../../../../../../../types/value';
import ConfirmModal from '../../../../../../../generic/modal/ConfirmModal';
import { COMPONENT_CONFIGURATION_LABELS, filterOptions } from '../../../../../../constants';
import { componentDataSourceFilter } from '../../../../../../utils';
import { usePageEditor } from '../../../../../PageEditorProvider';
import ConfigSection from '../../ConfigSection';
import FilterWhereConfig from './FilterWhereConfig';
import FilterUniqueConfig from './filter-unique-config/FilterUniqueConfig';

const defaultListFilterConfig: ListFilter = {
    comparisonType: 'AND',
    filterByProvidedObjects: false,
    filterId: null,
    limit: 0,
    listFilters: null,
    orderBy: null,
    orderByDirectionType: '',
    orderByTypeElementPropertyId: null,
    properties: null,
    searchCriteria: null,
    where: null,
};

interface Props {
    dataSourceConnector: ObjectDataRequest | null;
    dataSourceFilter: ListFilter | null;
    updateDataSourceFilter: (listFilter: ListFilter | null) => void;
}

const FilterConfig = ({ dataSourceConnector, dataSourceFilter, updateDataSourceFilter }: Props) => {
    const { container } = usePageEditor();
    const [confirmProps, setConfirmProps] = useState<Confirmation | null>(null);
    const { filter } = componentDataSourceFilter(dataSourceFilter);
    const selectedFilter = filter;

    // Reset the current filter configuration based on the selected filter type.
    const resetFilterConfiguration = (clickedOption: FilterType) => {
        const resetListFilterConfig =
            clickedOption === filterOptions['NONE']
                ? null
                : {
                      ...defaultListFilterConfig,
                      // Setting to empty {} or [] so they are picked up later in
                      // componentDataSourceFilter, so we can figure out what filter type
                      // was picked after reset.
                      filterId:
                          clickedOption === filterOptions['UNIQUE']
                              ? ({} as ValueElementIdAPI)
                              : null,
                      where: clickedOption === filterOptions['WHERE'] ? [] : null,
                  };

        updateDataSourceFilter(resetListFilterConfig);
    };

    const changeFilterOption = (event: ChangeEvent<HTMLInputElement>) => {
        const clickedOption = event.target.value as FilterType;

        // Show a warning confirmation modal that any existing filter config will be lost when
        // changing the used filter type since we can't have different types of filters
        // defined/active at the same time.
        if (clickedOption !== selectedFilter) {
            setConfirmProps({
                show: true,
                title: translations.PAGE_BUILDER_data_source_filter_modal_title,
                messages: [
                    'Changing Data Source Filter type will reset any existing filtering configuration.',
                ],
                buttonStyle: 'danger',
                buttonCaption: 'OK',
                onCancel: () => setConfirmProps(null),
                onConfirm: () => {
                    // Reset the current filter configuration based on the selected filter type.
                    resetFilterConfiguration(clickedOption);
                    setConfirmProps(null); // close modal
                },
                container,
            });
        }
    };

    const renderConfig = () =>
        selectedFilter !== filterOptions['NONE'] ? (
            selectedFilter === filterOptions['UNIQUE'] ? (
                <FilterUniqueConfig
                    dataSourceFilter={dataSourceFilter}
                    updateDataSourceFilter={updateDataSourceFilter}
                />
            ) : (
                <FilterWhereConfig
                    dataSourceConnector={dataSourceConnector}
                    dataSourceFilter={dataSourceFilter}
                    updateDataSourceFilter={updateDataSourceFilter}
                />
            )
        ) : null;

    return (
        <>
            <ConfigSection
                dataTestId="data-source-filter"
                title={COMPONENT_CONFIGURATION_LABELS['DATA_SOURCE_FILTER']}
            >
                <fieldset className="radio-button-group">
                    <label className="radio-button-label">
                        <input
                            className="radio-button-input"
                            type="radio"
                            id={filterOptions['NONE']}
                            value={filterOptions['NONE']}
                            onChange={changeFilterOption}
                            checked={selectedFilter === filterOptions['NONE']}
                        />
                        Do not filter
                    </label>
                    <label className="radio-button-label">
                        <input
                            className="radio-button-input"
                            type="radio"
                            id={filterOptions['UNIQUE']}
                            value={filterOptions['UNIQUE']}
                            onChange={changeFilterOption}
                            checked={selectedFilter === filterOptions['UNIQUE']}
                        />
                        Filter using a unique identifier
                    </label>
                    <label className="radio-button-label">
                        <input
                            className="radio-button-input"
                            type="radio"
                            id={filterOptions['WHERE']}
                            value={filterOptions['WHERE']}
                            onChange={changeFilterOption}
                            checked={selectedFilter === filterOptions['WHERE']}
                        />
                        Filter using a &apos;WHERE&apos; clause
                    </label>
                </fieldset>

                {renderConfig()}
            </ConfigSection>
            {confirmProps !== null ? <ConfirmModal {...confirmProps} /> : null}
        </>
    );
};

export default FilterConfig;
